import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

export interface OsmosLinkDialogData {
  href: string;
}

/** Protocols a quote description has any legitimate reason to link to. */
const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

/**
 * Returns the URL to store, or null if it should be refused.
 *
 * Tiptap already refuses dangerous protocols when the link is applied, so this
 * is not the security boundary — it exists so the person gets told why their
 * input was rejected instead of clicking Apply and seeing nothing happen.
 */
export function osmosNormalizeLinkHref(raw: string): string | null {
  const value = raw.trim();
  if (!value) {
    return null;
  }

  // Relative paths and in-page anchors carry no protocol and are fine.
  if (value.startsWith('/') || value.startsWith('#')) {
    return value;
  }

  // A bare domain is what people actually type. Assume https.
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }

  return ALLOWED_PROTOCOLS.includes(parsed.protocol.toLowerCase()) ? candidate : null;
}

/**
 * Link dialog for <osmos-editor>.
 *
 * Deliberately built from the app's own modal vocabulary rather than raw
 * Angular Material, so it reads as an Osmos dialog: bg-gris-1 body, centred
 * text-1 title, the far fa-times-circle close affordance top-right, and a
 * btn-2 / btn-1 footer pair. Same shape as
 * src/app/Dialogs/add-value-in-dropdown.
 *
 * Every label reuses a translation key that ALREADY exists in en, es and fr,
 * so this dialog adds no translation work:
 *   COMMON.Add / COMMON.Edit          Add / Edit
 *   QUOTE_TEMPLATE.URL                URL
 *   BUSINESS_PROFILE.Link/Url         Link/Url · Enlace/URL · Lien/URL
 *   QUOTE_TEMPLATE.Remove             Remove · Eliminar · Supprimer
 *   COMMON.Cancel / COMMON.Save       Cancel / Save
 *   ON_BOARDING_ERR.ERRORURLINVALID   Invalid URL · URL inválida · URL invalide
 *   ORGANIZATION.Enter link           Enter Link · Ingresar Enlace · Entrer le lien
 * If you change a label here, pick another existing key rather than adding
 * one, or three translation files need updating.
 */
@Component({
  selector: 'osmos-link-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.Default,
  template: `
    <div class="position-relative f-poppins osmos-link-dialog">
      <div class="modal-body bg-gris-1 pt-5">
        <h2 class="text-1 fs-4 text-center mb-0">
          {{ data.href ? ('COMMON.Edit' | translate) : ('COMMON.Add' | translate) }}
          {{ 'QUOTE_TEMPLATE.URL' | translate }}
        </h2>
        <div class="d-flex j-c-end">
          <i
            type="button"
            aria-label="Close"
            (click)="cancel()"
            class="color-4 fs-2 far fa-times-circle position-absolute end-0 top-0 m-3"
          ></i>
        </div>
      </div>

      <div class="modal-body bg-gris-1 px-5">
        <div class="mt-1">
          <label class="text-3 mb-2" for="osmosLinkHref">
            {{ 'BUSINESS_PROFILE.Link/Url' | translate }}<span class="color-6"> *</span>
          </label>
          <input
            id="osmosLinkHref"
            type="text"
            class="form-control"
            cdkFocusInitial
            autocomplete="off"
            placeholder="example.com"
            [(ngModel)]="href"
            (ngModelChange)="error = ''"
            (keyup.enter)="apply()"
          />
          <p class="color-6 mt-2 mb-0 osmos-link-dialog__error" *ngIf="error" role="alert">
            {{ error }}
          </p>
        </div>
      </div>

      <div class="modal-footer j-c-center mt-4 pb-4">
        <button type="button" class="btn btn-2 mx-2" *ngIf="data.href" (click)="remove()">
          {{ 'QUOTE_TEMPLATE.Remove' | translate }}
        </button>
        <button type="button" class="btn btn-2 mx-2" (click)="cancel()">
          {{ 'COMMON.Cancel' | translate }}
        </button>
        <button type="button" class="btn btn-1 mx-2" (click)="apply()">
          {{ 'COMMON.Save' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .osmos-link-dialog {
        min-width: 360px;
      }

      .osmos-link-dialog__hint {
        font-size: 12px;
        opacity: 0.75;
      }

      .osmos-link-dialog__error {
        font-size: 12px;
      }

      @media (max-width: 480px) {
        .osmos-link-dialog {
          min-width: 0;
        }
      }
    `,
  ],
})
export class OsmosLinkDialogComponent {
  href: string;
  error = '';

  constructor(
    private readonly dialogRef: MatDialogRef<OsmosLinkDialogComponent, string | null | undefined>,
    private readonly translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: OsmosLinkDialogData,
  ) {
    this.href = data?.href ?? '';
  }

  apply(): void {
    const normalized = osmosNormalizeLinkHref(this.href);
    if (!normalized) {
      // Both keys already exist in en/es/fr — see the note above the class.
      this.error = this.translate.instant(
        this.href.trim() ? 'ON_BOARDING_ERR.ERRORURLINVALID' : 'ORGANIZATION.Enter link',
      );
      return;
    }
    this.dialogRef.close(normalized);
  }

  /** null means "remove the link", as distinct from undefined for "cancel". */
  remove(): void {
    this.dialogRef.close(null);
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }
}
